/* eslint-disable @typescript-eslint/no-explicit-any */
import Vue from "vue";
import { Http } from "@status/codes";
import { ChatService } from "@/modules/Home/components/ChatWindow/Services/ChatWindow.service";
import { ChatMessages } from "@/modules/Home/components/ChatWindow/Model/ChatMessages.model";
import { ChatAttachment } from "@/modules/Home/components/ChatWindow/Model/ChatAttachment.model";
import { InputChat } from "@/modules/Home/components/ChatWindow/Model/InputChat.model";
import { AIViewContext } from "@/shared/models/AIViewContext";

export interface ChatAssistantConversation {
  messages: ChatMessages[];
  history: {
    user: string;
    ai: string;
    dateCreated: number;
    attachments?: ChatAttachment[];
  }[];
  sessionId: string;
  selectedDashboardId?: string;
  historyDashboardId?: string;
  summaryHistory: string;
  quickActions: any[];
  loading: boolean;
  error: boolean;
  hasUnreadResult: boolean;
  activeViewContext?: AIViewContext;
  activeViewLoading: boolean;
}

export interface ChatAssistantState {
  conversations: Record<string, ChatAssistantConversation>;
  activeConversationKey: string;
}

const emptyConversation = (): ChatAssistantConversation => ({
  messages: [],
  history: [],
  sessionId: "",
  selectedDashboardId: undefined,
  historyDashboardId: undefined,
  summaryHistory: "",
  quickActions: [],
  loading: false,
  error: false,
  hasUnreadResult: false,
  activeViewContext: undefined,
  activeViewLoading: false
});

const state: ChatAssistantState = {
  conversations: {},
  activeConversationKey: ""
};

const getConversation = (
  rootState: ChatAssistantState,
  key: string
): ChatAssistantConversation => {
  if (!rootState.conversations[key]) {
    Vue.set(rootState.conversations, key, emptyConversation());
  }
  return rootState.conversations[key];
};

const chatService = new ChatService();

export default {
  namespaced: true,
  state,
  getters: {
    conversation: (moduleState: ChatAssistantState) => (key: string) =>
      moduleState.conversations[key] || emptyConversation()
  },
  mutations: {
    setConversation(
      moduleState: ChatAssistantState,
      payload: { key: string; conversation: Partial<ChatAssistantConversation> }
    ) {
      const conversation = getConversation(moduleState, payload.key);
      Object.assign(conversation, payload.conversation);
    },
    setActiveConversation(moduleState: ChatAssistantState, key: string) {
      moduleState.activeConversationKey = key;
      const conversation = getConversation(moduleState, key);
      conversation.hasUnreadResult = false;
    },
    setActiveViewContext(
      moduleState: ChatAssistantState,
      payload: {
        key: string;
        context?: AIViewContext;
      }
    ) {
      const conversation = getConversation(moduleState, payload.key);
      conversation.activeViewContext = payload.context;
      conversation.activeViewLoading = false;
    },
    setActiveViewLoading(
      moduleState: ChatAssistantState,
      payload: { key: string; loading: boolean }
    ) {
      const conversation = getConversation(moduleState, payload.key);
      conversation.activeViewLoading = payload.loading;
      if (payload.loading) conversation.activeViewContext = undefined;
    },
    setSelectedDashboardId(
      moduleState: ChatAssistantState,
      payload: { key: string; dashboardId?: string }
    ) {
      const conversation = getConversation(moduleState, payload.key);
      conversation.selectedDashboardId = payload.dashboardId;
    },
    clearActiveViewContext(moduleState: ChatAssistantState, key: string) {
      const conversation = getConversation(moduleState, key);
      conversation.activeViewContext = undefined;
      conversation.activeViewLoading = false;
    },
    clearAllActiveViewContexts(moduleState: ChatAssistantState) {
      Object.keys(moduleState.conversations).forEach(key => {
        const conversation = moduleState.conversations[key];
        conversation.activeViewContext = undefined;
        conversation.activeViewLoading = false;
      });
    },
    appendMessage(
      moduleState: ChatAssistantState,
      payload: { key: string; message: ChatMessages }
    ) {
      getConversation(moduleState, payload.key).messages.push(payload.message);
    },
    appendChunk(
      moduleState: ChatAssistantState,
      payload: { key: string; chunk: string }
    ) {
      const conversation = getConversation(moduleState, payload.key);
      const message = conversation.messages[conversation.messages.length - 1];
      if (!message || message.role === "user") return;
      message.content =
        message.content === "Analizando..."
          ? payload.chunk
          : `${message.content || ""}${payload.chunk}`;
    },
    completeConversation(
      moduleState: ChatAssistantState,
      payload: {
        key: string;
        response: any;
        userText: string;
        attachments: ChatAttachment[];
      }
    ) {
      const conversation = getConversation(moduleState, payload.key);
      const responseData = payload.response.data || {};
      const assistantMessage =
        conversation.messages[conversation.messages.length - 1];
      const assistantText =
        responseData.finalResponseToUser?.text ||
        assistantMessage?.content ||
        "";

      if (assistantMessage) assistantMessage.content = assistantText;
      conversation.loading = false;
      conversation.error = payload.response.code !== Http.Ok;
      conversation.hasUnreadResult =
        payload.response.code === Http.Ok &&
        payload.key !== moduleState.activeConversationKey;

      if (payload.response.code !== Http.Ok) return;

      conversation.sessionId = responseData.sessionId || conversation.sessionId;
      conversation.summaryHistory =
        responseData.summaryHistory || conversation.summaryHistory;
      conversation.quickActions = (responseData.quickActions || []).filter(
        (action: any) => action && action.label && action.value
      );
      conversation.history.push({
        user:
          payload.userText +
          (payload.attachments.length > 0
            ? ` [Archivos adjuntos: ${payload.attachments
                .map(attachment => attachment.name)
                .join(", ")}]`
            : ""),
        ai: assistantText,
        dateCreated: Date.now(),
        attachments: payload.attachments
      });
    }
  },
  actions: {
    initializeConversation(
      { commit }: any,
      payload: { key: string; conversation: Partial<ChatAssistantConversation> }
    ) {
      commit("setConversation", payload);
    },
    markAsRead({ commit }: any, key: string) {
      commit("setActiveConversation", key);
    },
    async generate(
      { commit }: any,
      payload: {
        key: string;
        input: InputChat;
        functionCall:
          | "sendChatProp"
          | "getServiceSummary"
          | "getDocumentSummary";
        userText: string;
        attachments: ChatAttachment[];
      }
    ) {
      const assistantMessage: ChatMessages = {
        role: "assistant",
        content: "Analizando..."
      };

      commit("appendMessage", {
        key: payload.key,
        message: {
          role: "user",
          content: payload.userText,
          attachments: payload.attachments
        }
      });
      commit("appendMessage", { key: payload.key, message: assistantMessage });
      commit("setConversation", {
        key: payload.key,
        conversation: { loading: true, error: false }
      });

      const streamRequest =
        payload.functionCall === "getServiceSummary"
          ? chatService.getServiceSummary.bind(chatService)
          : payload.functionCall === "getDocumentSummary"
          ? chatService.getDocumentSummary.bind(chatService)
          : chatService.sendChatProp.bind(chatService);
      const response = await streamRequest(payload.input, chunk => {
        commit("appendChunk", { key: payload.key, chunk });
      });

      commit("completeConversation", {
        key: payload.key,
        response,
        userText: payload.userText,
        attachments: payload.attachments
      });

      return response;
    }
  }
};
